// Orchestrator for a single voice turn: speak → listen → transcribe.
// Owns an explicit state machine so callers (Intake screen, debug
// harness, future flows) never have to reason about overlapping
// promises from TTSPlayer + MicRecorder + TranscriptionSource.

const STATES = Object.freeze({
  IDLE: 'idle',
  UNLOCKED: 'unlocked',
  SPEAKING: 'speaking',
  LISTENING: 'listening',
  TRANSCRIBING: 'transcribing',
  ERROR: 'error',
  CANCELLED: 'cancelled',
});

const DEFAULT_MAX_LISTEN_MS = 120000; // 2 minutes — generous for intake reflection turns

export default class VoiceConversationStateMachine {
  constructor({
    audioSessionManager,
    ttsPlayer,
    ttsSource,
    micRecorder,
    transcriptionSource,
    telemetry,
  }) {
    if (!audioSessionManager) throw new Error('audioSessionManager required');
    if (!ttsPlayer) throw new Error('ttsPlayer required');
    if (!ttsSource) throw new Error('ttsSource required');
    if (!micRecorder) throw new Error('micRecorder required');
    if (!transcriptionSource) throw new Error('transcriptionSource required');

    this._asm = audioSessionManager;
    this._player = ttsPlayer;
    this._source = ttsSource;
    this._mic = micRecorder;
    this._stt = transcriptionSource;
    this._telemetry = telemetry || null;

    this._state = STATES.IDLE;
    this._listeners = new Map();
    this._lastError = null;
    this._lastTranscript = null;

    this._currentSpeakDeferred = null;
    this._currentListenDeferred = null;
    this._listenTimeoutId = null;
    this._transcribeAbortController = null;

    this._wirePlayerEvents();
    this._wireMicEvents();
  }

  getState() {
    return this._state;
  }

  getLastTranscript() {
    return this._lastTranscript;
  }

  getLastError() {
    return this._lastError;
  }

  on(event, callback) {
    if (!this._listeners.has(event)) this._listeners.set(event, new Set());
    this._listeners.get(event).add(callback);
  }

  off(event, callback) {
    const set = this._listeners.get(event);
    if (set) set.delete(callback);
  }

  _emit(event, payload) {
    const set = this._listeners.get(event);
    if (!set) return;
    for (const cb of set) {
      try {
        cb(payload);
      } catch (e) {
        console.error('[VoiceConversationStateMachine] listener threw:', e);
      }
    }
  }

  _transition(to, reason) {
    const from = this._state;
    if (!this._isLegal(from, to)) {
      throw new Error(
        `Illegal transition: ${from} → ${to} (reason: ${reason || 'n/a'})`
      );
    }
    this._state = to;
    const payload = { from, to, reason: reason || null };
    this._emit('stateChange', payload);
    if (this._telemetry) {
      this._telemetry.track('voice.stateChange', payload);
    }
  }

  _isLegal(from, to) {
    const allowed = LEGAL_TRANSITIONS[from];
    return Boolean(allowed && allowed.has(to));
  }

  // ── Public API ──────────────────────────────────────────────────

  async init() {
    if (this._state === STATES.UNLOCKED) return;
    if (this._state !== STATES.IDLE) {
      throw new Error(
        `init() can only be called from 'idle' — current state is '${this._state}'`
      );
    }
    await this._asm.unlock();
    this._transition(STATES.UNLOCKED, 'init');
  }

  async speak(text, voiceId) {
    if (this._state !== STATES.UNLOCKED) {
      throw new Error(`cannot speak from state ${this._state}`);
    }
    this._transition(STATES.SPEAKING, 'speak');
    this._emit('speakStart', { text, voiceId });
    this._telemetry?.track('voice.speakStart', { textLength: text?.length });

    const deferred = makeDeferred();
    this._currentSpeakDeferred = deferred;

    try {
      const stream = await this._source.stream(text, voiceId);
      // While we were awaiting the stream open, cancel/error may have
      // already moved us. Bail out if so.
      if (this._currentSpeakDeferred !== deferred) {
        return deferred.promise;
      }
      // Don't await this — player events drive the deferred. We do
      // need to surface synchronous throws though.
      this._player.play(stream).catch(() => {
        // 'error' / 'cancel' events already handle the deferred
      });
    } catch (error) {
      this._currentSpeakDeferred = null;
      this._lastError = error;
      this._transition(STATES.ERROR, 'speakError');
      this._emit('speakError', { error });
      this._emit('error', { error, context: 'speak' });
      this._telemetry?.track('voice.speakError', { message: error.message });
      deferred.reject(error);
    }

    return deferred.promise;
  }

  async listen(maxDurationMs = DEFAULT_MAX_LISTEN_MS) {
    if (this._state !== STATES.UNLOCKED) {
      throw new Error(`cannot listen from state ${this._state}`);
    }

    const deferred = makeDeferred();
    this._currentListenDeferred = deferred;

    this._transition(STATES.LISTENING, 'listen');
    this._emit('listenStart', { maxDurationMs });
    this._telemetry?.track('voice.listenStart', { maxDurationMs });

    try {
      await this._mic.start();
    } catch (error) {
      this._currentListenDeferred = null;
      this._lastError = error;
      this._transition(STATES.ERROR, 'micStartError');
      this._emit('listenError', { error });
      this._emit('error', { error, context: 'listen.start' });
      this._telemetry?.track('voice.listenError', { message: error.message });
      deferred.reject(error);
      return deferred.promise;
    }

    this._listenTimeoutId = setTimeout(() => {
      this._listenTimeoutId = null;
      if (this._state === STATES.LISTENING) {
        this.stopListening().catch(() => {});
      }
    }, maxDurationMs);

    return deferred.promise;
  }

  async stopListening() {
    if (this._state !== STATES.LISTENING) {
      throw new Error(`cannot stopListening from state ${this._state}`);
    }
    if (this._listenTimeoutId) {
      clearTimeout(this._listenTimeoutId);
      this._listenTimeoutId = null;
    }

    let stopResult;
    try {
      stopResult = await this._mic.stop();
    } catch (error) {
      this._failListen(error, 'mic.stop');
      return;
    }

    const { audioBlob, durationMs } = stopResult;
    this._emit('listenEnd', { durationMs });
    this._telemetry?.track('voice.listenEnd', { durationMs });

    this._transition(STATES.TRANSCRIBING, 'stopListening');
    this._emit('transcribeStart');
    this._telemetry?.track('voice.transcribeStart');

    this._transcribeAbortController = new AbortController();
    try {
      const result = await this._stt.transcribe(audioBlob, {
        signal: this._transcribeAbortController.signal,
      });
      this._transcribeAbortController = null;
      this._lastTranscript = result.text;
      this._emit('transcribeEnd', { text: result.text });
      this._telemetry?.track('voice.transcribeEnd', {
        textLength: result.text?.length,
      });
      this._transition(STATES.UNLOCKED, 'transcribeOk');
      this._emit('turnComplete', { transcript: result.text });
      this._telemetry?.track('voice.turnComplete', {
        textLength: result.text?.length,
      });
      const deferred = this._currentListenDeferred;
      this._currentListenDeferred = null;
      if (deferred) deferred.resolve({ text: result.text });
    } catch (error) {
      this._transcribeAbortController = null;
      this._failListen(error, 'transcribe');
    }
  }

  cancel() {
    const from = this._state;
    if (from === STATES.SPEAKING) {
      this._player.cancel();
      // 'cancel' event handler completes the deferred
    } else if (from === STATES.LISTENING) {
      if (this._listenTimeoutId) {
        clearTimeout(this._listenTimeoutId);
        this._listenTimeoutId = null;
      }
      this._mic.cancel();
      this._transition(STATES.CANCELLED, 'cancel');
      const deferred = this._currentListenDeferred;
      this._currentListenDeferred = null;
      if (deferred) deferred.reject(new Error('Cancelled'));
    } else if (from === STATES.TRANSCRIBING) {
      if (this._transcribeAbortController) {
        try {
          this._transcribeAbortController.abort();
        } catch {}
        this._transcribeAbortController = null;
      }
      this._transition(STATES.CANCELLED, 'cancel');
      const deferred = this._currentListenDeferred;
      this._currentListenDeferred = null;
      if (deferred) deferred.reject(new Error('Cancelled'));
    }
  }

  reset() {
    if (
      this._state !== STATES.ERROR &&
      this._state !== STATES.CANCELLED
    ) {
      throw new Error(`reset() not legal from state ${this._state}`);
    }
    this._lastError = null;
    this._transition(STATES.UNLOCKED, 'reset');
  }

  // ── Internal event wiring ───────────────────────────────────────

  _wirePlayerEvents() {
    this._player.on('audible', ({ ttfaMs }) => {
      if (this._state !== STATES.SPEAKING) return;
      this._emit('speakAudible', { ttfaMs });
      this._telemetry?.track('voice.speakAudible', { ttfaMs });
    });
    this._player.on('end', ({ totalMs, audioDurationMs }) => {
      if (this._state !== STATES.SPEAKING) return;
      this._emit('speakEnd', { totalMs, audioDurationMs });
      this._telemetry?.track('voice.speakEnd', { totalMs, audioDurationMs });
      this._transition(STATES.UNLOCKED, 'ttsEnd');
      const deferred = this._currentSpeakDeferred;
      this._currentSpeakDeferred = null;
      if (deferred) deferred.resolve();
    });
    this._player.on('error', ({ error }) => {
      if (this._state !== STATES.SPEAKING) return;
      this._lastError = error;
      this._transition(STATES.ERROR, 'ttsError');
      this._emit('speakError', { error });
      this._emit('error', { error, context: 'speak' });
      this._telemetry?.track('voice.speakError', { message: error.message });
      const deferred = this._currentSpeakDeferred;
      this._currentSpeakDeferred = null;
      if (deferred) deferred.reject(error);
    });
    this._player.on('cancel', () => {
      if (this._state !== STATES.SPEAKING) return;
      this._transition(STATES.CANCELLED, 'cancel');
      const deferred = this._currentSpeakDeferred;
      this._currentSpeakDeferred = null;
      if (deferred) deferred.reject(new Error('Cancelled'));
    });
  }

  _wireMicEvents() {
    this._mic.on('error', ({ error }) => {
      if (
        this._state !== STATES.LISTENING &&
        this._state !== STATES.TRANSCRIBING
      ) {
        return;
      }
      this._failListen(error, 'mic');
    });
  }

  _failListen(error, ctx) {
    if (this._listenTimeoutId) {
      clearTimeout(this._listenTimeoutId);
      this._listenTimeoutId = null;
    }
    this._lastError = error;
    const wasListening = this._state === STATES.LISTENING;
    const wasTranscribing = this._state === STATES.TRANSCRIBING;
    if (wasListening || wasTranscribing) {
      this._transition(STATES.ERROR, ctx + 'Error');
    }
    if (wasListening) {
      this._emit('listenError', { error });
      this._telemetry?.track('voice.listenError', { message: error.message });
    } else if (wasTranscribing) {
      this._emit('transcribeError', { error });
      this._telemetry?.track('voice.transcribeError', {
        message: error.message,
      });
    }
    this._emit('error', { error, context: ctx });
    const deferred = this._currentListenDeferred;
    this._currentListenDeferred = null;
    if (deferred) deferred.reject(error);
  }
}

// Legal transitions out of each state (target → allowed)
const LEGAL_TRANSITIONS = {
  [STATES.IDLE]: new Set([STATES.UNLOCKED]),
  [STATES.UNLOCKED]: new Set([STATES.SPEAKING, STATES.LISTENING]),
  [STATES.SPEAKING]: new Set([
    STATES.UNLOCKED,
    STATES.CANCELLED,
    STATES.ERROR,
  ]),
  [STATES.LISTENING]: new Set([
    STATES.TRANSCRIBING,
    STATES.CANCELLED,
    STATES.ERROR,
  ]),
  [STATES.TRANSCRIBING]: new Set([
    STATES.UNLOCKED,
    STATES.CANCELLED,
    STATES.ERROR,
  ]),
  [STATES.ERROR]: new Set([STATES.UNLOCKED]),
  [STATES.CANCELLED]: new Set([STATES.UNLOCKED]),
};

function makeDeferred() {
  let resolve, reject;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve, reject };
}
